import { Fragment, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { apiFetch } from '../lib/supabase'

const SUPERADMINS = new Set(['lxxmng@gmail.com', 'lxxmng2@gmail.com', 'dr.reenforce@gmail.com'])

interface CarrierRow {
  carrier_code: string
  carrier_name: string
  status: string
  source_type: string
  cadence: string
  enabled: boolean
  connector_job: string | null
  connector_key: string | null
  transport: string | null
  re_notes: string | null
  future_sailings: number
  sources: string
  cutoff_pct: number
  has_connector: boolean
  has_gocomet: boolean
  consecutive_failures: number
  last_run_at: string | null
  last_run_status: string | null
  last_run_sailings: number | null
  last_run_error: string | null
}

interface Summary {
  total_sailings: number
  live_carriers: number
  direct_connectors: number
  gocomet_carriers: number
  with_cutoffs: number
  degraded: number
}

interface Coverage {
  summary: Summary
  carriers: CarrierRow[]
  refreshed_at: string
}

// "has a way to get schedules at all" — a wired connector, aggregator coverage, or actual rows.
function hasSource(c: CarrierRow) {
  return Boolean(c.connector_job) || c.has_connector || c.has_gocomet || c.future_sailings > 0
}

function statusBadge(c: CarrierRow) {
  if (!c.enabled) {
    // Surface WHY it's off instead of a flat "disabled":
    //  · blocked        → source is paid / login-gated, deliberately parked
    //  · no source yet  → needs reverse-engineering to build a fetcher
    //  · otherwise      → intentionally disabled (source exists, just switched off)
    if (c.status === 'blocked')
      return (
        <span className="badge badge-red" title="Source is paid / login-gated. Parked">
          parked
        </span>
      )
    if (c.status === 'needs_re' || !hasSource(c))
      return (
        <span
          className="badge badge-amber"
          title="No schedule source yet. Needs reverse-engineering"
        >
          needs RE
        </span>
      )
    return <span className="badge badge-gray">disabled</span>
  }
  if (c.status === 'blocked') return <span className="badge badge-red">blocked</span>
  if (c.status === 'planned') return <span className="badge badge-purple">planned</span>
  if (c.status === 'needs_re') return <span className="badge badge-amber">needs RE</span>
  if (c.enabled && c.future_sailings < 20 && c.status === 'live')
    return (
      <span className="badge badge-red inline-flex items-center gap-1">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          aria-hidden
          role="presentation"
        >
          <path d="M12 3l9 16H3L12 3Z" />
          <path d="M12 10v4M12 17h.01" />
        </svg>
        degraded
      </span>
    )
  return <span className="badge badge-green">live</span>
}

function sourcePills(c: CarrierRow) {
  return (
    <span className="flex gap-1 flex-wrap">
      {c.has_connector && <span className="pill pill-blue">connector</span>}
      {c.has_gocomet && <span className="pill pill-purple">GoComet</span>}
      {!c.has_connector && !c.has_gocomet && c.sources && (
        <span className="pill pill-gray">{c.sources}</span>
      )}
    </span>
  )
}

function cadenceBadge(c: CarrierRow) {
  if (c.source_type === 'seed') return <span className="pill pill-teal">weekly seed</span>
  if (c.cadence === 'weekly') return <span className="pill pill-blue">weekly</span>
  return <span className="pill pill-amber">daily</span>
}

function fmt(n: number) {
  return n.toLocaleString()
}

type SortKey = 'name' | 'status' | 'enabled' | 'cadence' | 'sailings' | 'cutoffs' | 'lastrun'

// Sortable column header — persistent up/down arrows sit on every sortable column so it's obvious
// they sort; the active column highlights the current direction in the signal colour. Click to sort,
// click again to flip. (Matches the Compare-lanes table sort style.)
function SortTh({
  k,
  label,
  align,
  sortKey,
  sortDir,
  onSort,
}: {
  k: SortKey
  label: string
  align?: 'right'
  sortKey: SortKey
  sortDir: 'asc' | 'desc'
  onSort: (k: SortKey) => void
}) {
  const active = sortKey === k
  return (
    <th style={align ? { textAlign: 'right' } : undefined}>
      <button
        type="button"
        onClick={() => onSort(k)}
        title={`Sort by ${label}`}
        className={`inline-flex items-center gap-1 ${align ? 'flex-row-reverse' : ''} hover:text-[var(--text-primary)] transition-colors ${active ? 'text-[var(--text-primary)] font-semibold' : ''}`}
      >
        {label}
        <span
          aria-hidden
          className="inline-flex flex-col text-[7px]"
          style={{ lineHeight: '0.75' }}
        >
          <span
            style={{
              color: active && sortDir === 'asc' ? 'var(--signal)' : 'var(--text-muted)',
              opacity: active && sortDir === 'asc' ? 1 : 0.45,
            }}
          >
            ▲
          </span>
          <span
            style={{
              color: active && sortDir === 'desc' ? 'var(--signal)' : 'var(--text-muted)',
              opacity: active && sortDir === 'desc' ? 1 : 0.45,
            }}
          >
            ▼
          </span>
        </span>
      </button>
    </th>
  )
}

export function AdminPage() {
  const { session, loading } = useAuth()
  const nav = useNavigate()
  const [data, setData] = useState<Coverage | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [fetching, setFetching] = useState(true)
  const [filter, setFilter] = useState<'all' | 'live' | 'degraded' | 'planned'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<string | null>(null)

  const email = session?.user?.email?.toLowerCase() ?? ''
  const isAdmin = SUPERADMINS.has(email)

  useEffect(() => {
    if (!loading && !session) nav('/login')
  }, [loading, session, nav])

  const load = useCallback(async () => {
    setFetching(true)
    const r = await apiFetch('/admin/carrier-coverage')
    if (r.ok) {
      setData(r.json.data as Coverage)
      setErr(null)
    } else {
      setErr(r.json?.error?.message ?? 'Failed to load')
    }
    setFetching(false)
  }, [])

  useEffect(() => {
    if (session && isAdmin) load()
  }, [session, isAdmin, load])

  // Clicking a sort key: same key flips direction, new key resets to that key's natural direction
  // (name → A–Z, everything else → biggest/most-recent first).
  const onSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      // name/cadence read best A–Z; the rest read best biggest/most-recent/on-first.
      setSortDir(key === 'name' || key === 'cadence' ? 'asc' : 'desc')
      return key
    })
  }, [])

  // Enable/disable a connector from the panel (PATCH the registry). Optimistic flip, then reload to
  // reflect the server (and any cadence/status recompute). Disabled while a request is in flight.
  const toggleEnabled = useCallback(async (code: string, next: boolean) => {
    setBusy((b) => new Set(b).add(code))
    setData((d) =>
      d
        ? {
            ...d,
            carriers: d.carriers.map((c) =>
              c.carrier_code === code ? { ...c, enabled: next } : c
            ),
          }
        : d
    )
    const r = await apiFetch(`/admin/schedule-connectors/${code}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled: next }),
    })
    if (!r.ok) {
      // revert on failure
      setData((d) =>
        d
          ? {
              ...d,
              carriers: d.carriers.map((c) =>
                c.carrier_code === code ? { ...c, enabled: !next } : c
              ),
            }
          : d
      )
      setErr(r.json?.error?.message ?? 'Toggle failed')
    }
    setBusy((b) => {
      const n = new Set(b)
      n.delete(code)
      return n
    })
  }, [])

  if (loading || (session && isAdmin && fetching && !data)) {
    return <main className="max-w-6xl mx-auto px-6 pt-20 text-[var(--text-muted)]">Loading…</main>
  }

  if (!isAdmin) {
    return (
      <main className="max-w-6xl mx-auto px-6 pt-20">
        <p className="text-sm text-[var(--text-muted)]">Access restricted.</p>
      </main>
    )
  }

  if (err) {
    return (
      <main className="max-w-6xl mx-auto px-6 pt-20">
        <p className="text-sm text-danger">{err}</p>
        <button type="button" onClick={load} className="mt-2 text-sm underline">
          Retry
        </button>
      </main>
    )
  }

  const carriers = data?.carriers ?? []
  // status rank for sorting: live-and-healthy first, degraded, planned/blocked, disabled last.
  const statusRank = (c: CarrierRow) => {
    if (!c.enabled) return 4
    if (c.status === 'blocked' || c.status === 'needs_re' || c.status === 'planned') return 3
    if (c.future_sailings < 20 && c.status === 'live') return 2 // degraded
    if (c.status === 'live') return 0
    return 1
  }
  const dir = sortDir === 'asc' ? 1 : -1
  const visible = carriers
    .filter((c) => {
      if (filter === 'live') return c.status === 'live' && c.enabled
      if (filter === 'degraded') return c.enabled && c.future_sailings < 20 && c.status === 'live'
      if (filter === 'planned')
        return c.status === 'planned' || c.status === 'needs_re' || c.status === 'blocked'
      return true
    })
    .sort((a, b) => {
      const cadRank = (c: CarrierRow) =>
        c.source_type === 'seed' ? 2 : c.cadence === 'weekly' ? 1 : 0
      let cmp = 0
      if (sortKey === 'name') cmp = a.carrier_name.localeCompare(b.carrier_name)
      else if (sortKey === 'sailings') cmp = a.future_sailings - b.future_sailings
      else if (sortKey === 'status')
        cmp = statusRank(b) - statusRank(a) // higher rank = worse
      else if (sortKey === 'enabled') cmp = Number(a.enabled) - Number(b.enabled)
      else if (sortKey === 'cadence') cmp = cadRank(a) - cadRank(b)
      else if (sortKey === 'cutoffs') cmp = a.cutoff_pct - b.cutoff_pct
      else if (sortKey === 'lastrun')
        cmp = new Date(a.last_run_at ?? 0).getTime() - new Date(b.last_run_at ?? 0).getTime()
      // stable tiebreak by name so equal keys don't jitter
      return (cmp || a.carrier_name.localeCompare(b.carrier_name)) * dir
    })

  const s = data?.summary

  return (
    <main className="max-w-6xl mx-auto px-6 pt-12 pb-20">
      <style>{`
        .badge { display:inline-block; font-size:11px; font-weight:500; padding:2px 8px; border-radius:100px; }
        .badge-green  { background:rgb(var(--signal-rgb) / 0.12); color:var(--signal); }
        .badge-amber  { background:rgb(var(--warn-rgb) / 0.12); color:var(--warn); }
        .badge-red    { background:rgb(var(--danger-rgb) / 0.12); color:var(--danger); }
        .badge-gray   { background:var(--surface-2); color:var(--text-secondary); }
        .badge-purple { background:rgb(var(--accent-rgb) / 0.12); color:var(--accent); }
        .pill { display:inline-block; font-size:10px; font-weight:500; padding:1px 6px; border-radius:100px; }
        .pill-blue    { background:rgb(var(--signal-rgb) / 0.12); color:var(--signal); }
        .pill-purple  { background:rgb(var(--accent-rgb) / 0.12); color:var(--accent); }
        .pill-teal    { background:rgb(var(--accent-rgb) / 0.12); color:var(--accent); }
        .pill-amber   { background:rgb(var(--warn-rgb) / 0.12); color:var(--warn); }
        .pill-gray    { background:var(--surface-2); color:var(--text-secondary); }
        .tbl { width:100%; border-collapse:collapse; font-size:12px; }
        .tbl th { font-size:11px; font-weight:500; color:var(--text-muted); text-align:left; padding:6px 8px 4px; border-bottom:1px solid var(--border); white-space:nowrap; }
        .tbl td { padding:6px 8px; border-bottom:1px solid var(--border); vertical-align:middle; }
        .tbl tr:last-child td { border-bottom:none; }
        .tbl tr.degraded-row td { background:rgb(var(--danger-rgb) / 0.08); }
        .mono { font-family:var(--font-mono,monospace); font-size:11px; }
        .toggle { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; padding:2px 9px 2px 6px; border-radius:100px; border:1px solid transparent; cursor:pointer; transition:background .15s; }
        .toggle-dot { width:8px; height:8px; border-radius:100px; background:currentColor; }
        .toggle-on  { background:rgb(var(--signal-rgb) / 0.15); color:var(--signal); }
        .toggle-on:hover  { background:rgb(var(--signal-rgb) / 0.24); }
        .toggle-off { background:var(--surface-2); color:var(--text-secondary); }
        .toggle-off:hover { background:rgb(var(--surface-2-rgb) / 0.7); }
        .toggle:disabled { cursor:default; }
      `}</style>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="font-display text-2xl"
            style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
          >
            Carrier coverage
          </h1>
          {data && (
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Last refreshed {new Date(data.refreshed_at).toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => nav('/admin/schedules')}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] hover:bg-[var(--surface-2)] font-medium"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              aria-hidden
              role="presentation"
            >
              <path d="M4 8h16M7 3v3M17 3v3M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
            </svg>
            Schedules &amp; Freshness →
          </button>
          <button
            type="button"
            onClick={load}
            disabled={fetching}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-[var(--surface-1)] disabled:opacity-50"
          >
            {fetching ? (
              'Refreshing…'
            ) : (
              <>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  aria-hidden
                  role="presentation"
                >
                  <path d="M4 12a8 8 0 0 1 13.7-5.7L20 8M20 4v4h-4" />
                  <path d="M20 12a8 8 0 0 1-13.7 5.7L4 16M4 20v-4h4" />
                </svg>
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 mb-6">
          {[
            { label: 'future sailings', value: fmt(s.total_sailings) },
            { label: 'live carriers', value: s.live_carriers },
            { label: 'direct connectors', value: s.direct_connectors },
            { label: 'GoComet carriers', value: s.gocomet_carriers },
            { label: 'with cutoffs', value: s.with_cutoffs },
            { label: 'degraded', value: s.degraded, warn: s.degraded > 0 },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl bg-[var(--surface-1)] p-3">
              <div className="text-[10px] text-[var(--text-muted)] mb-1">{stat.label}</div>
              <div className={`text-xl font-semibold ${stat.warn ? 'text-danger' : ''}`}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(['all', 'live', 'degraded', 'planned'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              filter === f
                ? 'border-[var(--signal)] bg-[var(--signal)] text-on-accent'
                : 'border-[var(--border)] hover:bg-[var(--surface-1)]'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-[var(--border)] overflow-hidden">
        <table className="tbl">
          <thead>
            <tr>
              <SortTh
                k="name"
                label="carrier"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <SortTh
                k="status"
                label="status"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <SortTh
                k="enabled"
                label="enabled"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <SortTh
                k="cadence"
                label="cadence"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <SortTh
                k="sailings"
                label="sailings"
                align="right"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <th>sources</th>
              <SortTh
                k="cutoffs"
                label="cutoffs"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <SortTh
                k="lastrun"
                label="last run"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <th>notes</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-[var(--text-muted)] py-6">
                  No carriers match this filter.
                </td>
              </tr>
            )}
            {visible.map((c) => {
              const isOpen = expanded === c.carrier_code
              return (
                <Fragment key={c.carrier_code}>
                  <tr
                    className={
                      c.enabled && c.future_sailings < 20 && c.status === 'live'
                        ? 'degraded-row'
                        : ''
                    }
                  >
                    <td>
                      <div className="font-medium text-xs">{c.carrier_name}</div>
                      <div className="mono text-[var(--text-muted)]">{c.carrier_code}</div>
                    </td>
                    <td>{statusBadge(c)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => toggleEnabled(c.carrier_code, !c.enabled)}
                        disabled={busy.has(c.carrier_code)}
                        title={
                          !c.enabled && !c.connector_job
                            ? 'No connector wired. Enabling registers it but won’t fetch schedules until a connector job exists'
                            : c.enabled
                              ? 'Click to disable (orchestrator/ingest will skip this line)'
                              : 'Click to enable (line will run on its cadence)'
                        }
                        className={`toggle ${c.enabled ? 'toggle-on' : 'toggle-off'} ${busy.has(c.carrier_code) ? 'opacity-50' : ''}`}
                      >
                        <span className="toggle-dot" />
                        {c.enabled ? 'on' : 'off'}
                      </button>
                    </td>
                    <td>{cadenceBadge(c)}</td>
                    <td style={{ textAlign: 'right' }} className="mono">
                      {c.future_sailings > 0 ? fmt(c.future_sailings) : '—'}
                    </td>
                    <td>{sourcePills(c)}</td>
                    <td className="mono text-[var(--text-muted)]">
                      {c.cutoff_pct > 0 ? `${c.cutoff_pct}%` : '—'}
                    </td>
                    <td className="text-[10px] text-[var(--text-muted)]">
                      {c.last_run_at ? (
                        <span>
                          {new Date(c.last_run_at).toLocaleDateString()}{' '}
                          <span
                            className={
                              c.last_run_status === 'success' ? 'text-signal' : 'text-danger'
                            }
                          >
                            {c.last_run_status}
                          </span>
                          {c.last_run_sailings != null && (
                            <span className="ml-1 text-[var(--text-muted)]">
                              ({c.last_run_sailings})
                            </span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-[10px] text-[var(--text-muted)]">
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : c.carrier_code)}
                        className="flex items-center gap-1 max-w-[220px] text-left hover:text-[var(--text-primary)]"
                        title={isOpen ? 'Hide details' : 'Show full notes & details'}
                      >
                        <span className={isOpen ? '' : 'truncate'}>{c.re_notes ?? '—'}</span>
                        <span aria-hidden className="shrink-0">
                          {isOpen ? '▾' : '▸'}
                        </span>
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={9} className="bg-[var(--surface-1)]">
                        <div className="px-2 py-3 grid gap-x-8 gap-y-2 sm:grid-cols-2 text-[11px]">
                          <div className="sm:col-span-2">
                            <div className="text-[var(--text-muted)] mb-0.5">Notes</div>
                            <div className="whitespace-pre-wrap">{c.re_notes ?? '—'}</div>
                          </div>
                          <div>
                            <span className="text-[var(--text-muted)]">Connector: </span>
                            <span className="mono">
                              {c.connector_key ?? c.connector_job ?? '— (none wired)'}
                            </span>
                            {c.transport && (
                              <span className="text-[var(--text-muted)]"> · {c.transport}</span>
                            )}
                          </div>
                          <div>
                            <span className="text-[var(--text-muted)]">Source type: </span>
                            <span className="mono">{c.source_type}</span>
                          </div>
                          <div>
                            <span className="text-[var(--text-muted)]">Sources: </span>
                            <span className="mono">{c.sources || '—'}</span>
                          </div>
                          <div>
                            <span className="text-[var(--text-muted)]">Consecutive failures: </span>
                            <span
                              className={`mono ${c.consecutive_failures > 0 ? 'text-danger' : ''}`}
                            >
                              {c.consecutive_failures}
                            </span>
                          </div>
                          {c.last_run_error && (
                            <div className="sm:col-span-2">
                              <span className="text-[var(--text-muted)]">Last run error: </span>
                              <span className="mono text-danger">{c.last_run_error}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {data && (
        <p className="mt-3 text-[10px] text-[var(--text-muted)]">
          Showing {visible.length} of {carriers.length} carriers ·{' '}
          <button type="button" onClick={load} className="underline">
            refresh
          </button>
        </p>
      )}
    </main>
  )
}
